import React from "react";
import { Sparkles, CircleDot, Check } from "lucide-react";

// ---------------------------------------------------------------------------
// Sub-components used inside the mock window
// ---------------------------------------------------------------------------
const MockChromeBar = ({ centerLabel, rightLabel }) => (
  <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/80">
    <div className="flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
      <span className="w-2.5 h-2.5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
      <span className="w-2.5 h-2.5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
    </div>
    {centerLabel && (
      <div className="text-[11.5px] text-neutral-500 dark:text-neutral-400 font-mono">
        {centerLabel}
      </div>
    )}
    {rightLabel && (
      <div className="text-[11.5px] text-neutral-400 dark:text-neutral-500">{rightLabel}</div>
    )}
  </div>
);

const SidebarItem = ({ item }) => (
  <div
    className={`flex items-center gap-2 px-2.5 py-2 rounded-md text-[13px] mb-1 ${
      item.active
        ? "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900"
        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
    }`}
  >
    {item.done ? (
      <Check className="w-3.5 h-3.5 opacity-70" strokeWidth={2.5} />
    ) : (
      <CircleDot className="w-3.5 h-3.5 opacity-70" strokeWidth={2} />
    )}
    {item.label}
  </div>
);

const MockSidebar = ({ title, items, suggestion }) => (
  <aside className="hidden md:block col-span-3 border-r border-neutral-100 dark:border-neutral-800 p-5 bg-neutral-50/40 dark:bg-neutral-900/40">
    <div className="text-[11px] uppercase tracking-widest text-neutral-400 dark:text-neutral-500 mb-3">
      {title}
    </div>
    {items.map((item) => (
      <SidebarItem key={item.label} item={item} />
    ))}
    {suggestion && (
      <div className="mt-6 p-3 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
        <div className="flex items-center gap-1.5 text-[11.5px] text-neutral-500 dark:text-neutral-400">
          <Sparkles className="w-3.5 h-3.5" />
          {suggestion.label}
        </div>
        <div className="mt-1.5 text-[12.5px] text-neutral-800 dark:text-neutral-200 leading-snug">
          {suggestion.body}
        </div>
      </div>
    )}
  </aside>
);

const MetricCard = ({ metric }) => (
  <div className="p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:border-neutral-300 dark:hover:border-neutral-700 transition-colors duration-200">
    <div className="text-[11.5px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
      {metric.t}
    </div>
    <div className="mt-2 text-[24px] font-medium tracking-tight text-neutral-900 dark:text-neutral-50">
      {metric.v}
    </div>
    <div className="text-[12px] text-neutral-500 dark:text-neutral-400">{metric.d}</div>
  </div>
);

const FlowStep = ({ label, active }) => (
  <div
    className={`shrink-0 px-3 py-1.5 rounded-md text-[12.5px] border ${
      active
        ? "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 border-neutral-900 dark:border-white"
        : "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-800"
    }`}
  >
    {label}
  </div>
);

const FlowRow = ({ title, steps, activeIndex }) => (
  <div className="mt-6 p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-gradient-to-b from-white dark:from-neutral-900 to-neutral-50/60 dark:to-neutral-950/60">
    <div className="text-[11.5px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-3">
      {title}
    </div>
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      {steps.map((label, idx) => (
        <React.Fragment key={label}>
          <FlowStep label={label} active={idx === activeIndex} />
          {idx < steps.length - 1 && (
            <div className="shrink-0 w-6 h-px bg-neutral-300 dark:bg-neutral-700" />
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const MainPanel = ({ main }) => (
  <div className="col-span-12 md:col-span-9 p-6 lg:p-8">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
          {main.stepLabel}
        </div>
        <div className="mt-1 text-[18px] font-medium tracking-tight text-neutral-900 dark:text-neutral-50">
          {main.stepTitle}
        </div>
      </div>
      <div className="flex items-center gap-2 text-[12px] text-neutral-500 dark:text-neutral-400">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Live preview
      </div>
    </div>

    <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
      {main.metrics.map((m) => (
        <MetricCard key={m.t} metric={m} />
      ))}
    </div>

    <FlowRow title={main.flowTitle} steps={main.flow} activeIndex={main.flowActive} />
  </div>
);

const FloatingCardLeft = ({ card, widthClass = "w-[220px]" }) => (
  <div
    className={`hidden md:block absolute -left-6 lg:-left-10 top-24 ${widthClass} rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.12)] dark:shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)] p-3.5 rotate-[-3deg]`}
  >
    <div className="text-[10.5px] uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
      {card.label}
    </div>
    <div className="mt-1 text-[13px] font-medium text-neutral-900 dark:text-neutral-50">
      {card.title}
    </div>
    <div className="mt-2 text-[11.5px] text-neutral-500 dark:text-neutral-400 leading-snug">
      {card.body}
    </div>
  </div>
);

const FloatingCardRight = ({ card, widthClass = "w-[210px]" }) => (
  <div
    className={`hidden md:block absolute -right-4 lg:-right-8 bottom-10 ${widthClass} rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.12)] dark:shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)] p-3.5 rotate-[3deg]`}
  >
    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
      <Sparkles className="w-3 h-3" /> {card.label}
    </div>
    <div className="mt-1 text-[12.5px] text-neutral-800 dark:text-neutral-200 leading-snug">
      {card.body}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main composed component
// ---------------------------------------------------------------------------
const ProductMock = ({
  chromeCenter,
  chromeRight = "Auto-saved",
  sidebar,
  main,
  leftCard,
  rightCard,
  leftCardWidth,
  rightCardWidth,
  className = "",
}) => (
  <div className={`relative ${className}`}>
    <div className="relative max-w-[1080px] mx-auto">
      <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.18)] dark:shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] overflow-hidden">
        <MockChromeBar centerLabel={chromeCenter} rightLabel={chromeRight} />
        <div className="grid grid-cols-12">
          <MockSidebar
            title={sidebar.title}
            items={sidebar.items}
            suggestion={sidebar.suggestion}
          />
          <MainPanel main={main} />
        </div>
      </div>
      {leftCard && <FloatingCardLeft card={leftCard} widthClass={leftCardWidth} />}
      {rightCard && <FloatingCardRight card={rightCard} widthClass={rightCardWidth} />}
    </div>
  </div>
);

export default ProductMock;
